import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SupportMetricsCard from './components/SupportMetricsCard';
import TicketVolumeChart from './components/TicketVolumeChart';
import PriorityDistributionChart from './components/PriorityDistributionChart';
import AgentWorkloadCard from './components/AgentWorkloadCard';
import TicketAnalyticsTable from './components/TicketAnalyticsTable';
import AdminActivityAudit from './components/AdminActivityAudit';

const SupportOperationsIntelligence = () => {
  const [activeTab, setActiveTab] = useState('tickets');
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate data refresh
    setTimeout(() => setRefreshing(false), 2000);
  };

  const supportMetrics = [
    {
      title: "Open Tickets",
      value: "116",
      change: "+8.2%",
      changeType: "negative",
      icon: "Ticket",
      target: "100"
    },
    {
      title: "Avg Resolution Time",
      value: "2.4",
      unit: "h",
      change: "-12.5%",
      changeType: "positive",
      icon: "Clock",
      target: "3.0"
    },
    {
      title: "SLA Breach Rate",
      value: "3.2",
      unit: "%",
      change: "+0.8%",
      changeType: "negative",
      icon: "AlertTriangle",
      target: "5.0"
    },
    {
      title: "Customer Satisfaction",
      value: "4.7",
      unit: "/5",
      change: "+0.2",
      changeType: "positive",
      icon: "Star",
      target: "4.5"
    }
  ];

  const tabs = [
    { id: 'tickets', label: 'Ticket Analytics', icon: 'Table' },
    { id: 'audit', label: 'Admin Activity', icon: 'Shield' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Support & Operations Intelligence - SuperAdmin Dashboard</title>
        <meta name="description" content="Comprehensive support analytics and administrative activity monitoring for optimized service delivery and platform health tracking." />
      </Helmet>

      <Header />
      <Sidebar />

      <main className="lg:ml-60 pt-16 lg:pt-0">
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-accent to-warning rounded-lg flex items-center justify-center">
                  <Icon name="HeadphonesIcon" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">Support & Operations Intelligence</h1>
                  <p className="text-text-secondary">Customer support analytics and administrative activity monitoring</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 px-3 py-2 bg-card border border-border rounded-lg">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                <span className="text-sm text-text-secondary">Live Updates</span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                iconName={refreshing ? "Loader2" : "RefreshCw"}
                className={refreshing ? "animate-spin" : ""}
              >
                {refreshing ? "Refreshing..." : "Refresh"}
              </Button>

              <div className="flex items-center space-x-1 bg-card border border-border rounded-lg p-1">
                <Button variant="ghost" size="sm" iconName="Filter">
                  Filter
                </Button>
                <Button variant="ghost" size="sm" iconName="Download">
                  Export
                </Button>
              </div>
            </div>
          </div>

          {/* Support Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {supportMetrics.map((metric, index) => (
              <SupportMetricsCard
                key={index}
                title={metric.title}
                value={metric.value}
                unit={metric.unit}
                change={metric.change}
                changeType={metric.changeType}
                icon={metric.icon}
                target={metric.target}
              />
            ))}
          </div>

          {/* Main Analytics Section */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Ticket Volume Chart */}
            <div className="xl:col-span-2">
              <TicketVolumeChart />
            </div>

            {/* Priority Distribution */}
            <div className="space-y-6">
              <PriorityDistributionChart />
            </div>
          </div>

          {/* Agent Workload Section */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            <div className="xl:col-span-1">
              <AgentWorkloadCard />
            </div>
            
            {/* Real-time Alerts */}
            <div className="xl:col-span-3">
              <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 dashboard-fade-in">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
                    <Icon name="Bell" size={20} className="text-error" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">Real-time Alerts</h3>
                    <p className="text-sm text-text-secondary">Critical issues requiring immediate attention</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-error/10 border border-error/20 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="AlertTriangle" size={20} className="text-error" />
                      <div>
                        <p className="text-sm font-medium text-text-primary">SLA Breach Alert</p>
                        <p className="text-xs text-text-secondary">TKT-2025-001 exceeded response time limit</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View Ticket
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="Clock" size={20} className="text-warning" />
                      <div>
                        <p className="text-sm font-medium text-text-primary">High Priority Queue</p>
                        <p className="text-xs text-text-secondary">5 high priority tickets awaiting assignment</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Assign
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/20 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="TrendingUp" size={20} className="text-primary" />
                      <div>
                        <p className="text-sm font-medium text-text-primary">Volume Spike Detected</p>
                        <p className="text-xs text-text-secondary">40% increase in ticket volume in the last hour</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Analyze
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabbed Content Section */}
          <div className="bg-card border border-border rounded-lg shadow-elevation-1">
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                      activeTab === tab.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'tickets' && <TicketAnalyticsTable />}
              {activeTab === 'audit' && <AdminActivityAudit />}
            </div>
          </div>

          {/* Performance Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 dashboard-fade-in">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                </div>
                <h3 className="text-sm font-medium text-text-secondary">Resolution Rate</h3>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-text-primary">94.2%</div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-success h-2 rounded-full" style={{ width: '94.2%' }} />
                </div>
                <div className="text-xs text-text-secondary">Target: 90%</div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 dashboard-fade-in">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={16} className="text-primary" />
                </div>
                <h3 className="text-sm font-medium text-text-secondary">Active Agents</h3>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-text-primary">5/8</div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '62.5%' }} />
                </div>
                <div className="text-xs text-text-secondary">62.5% capacity</div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 dashboard-fade-in">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={16} className="text-warning" />
                </div>
                <h3 className="text-sm font-medium text-text-secondary">First Response</h3>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-text-primary">1.2h</div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-warning h-2 rounded-full" style={{ width: '80%' }} />
                </div>
                <div className="text-xs text-text-secondary">Target: 1.5h</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation Spacer */}
      <div className="h-20 lg:hidden" />
    </div>
  );
};

export default SupportOperationsIntelligence;